import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import ClientMarquee from "@/components/ClientMarquee";
import { useSettings } from "@/contexts/SettingsContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";

export default function About() {
  const { settings } = useSettings();
  usePageTitle("about");
  if (!settings) return null;

  const TEAM = [
    { name: "Production", role: "On-site delivery", color: "bg-ot-peach" },
    { name: "Creative", role: "Activation design", color: "bg-ot-coral" },
    { name: "Tech", role: "Hardware & software", color: "bg-ot-mint" },
    { name: "Account", role: "Client success", color: "bg-ot-muted" },
  ];

  const headline = settings.about_headline || "We've been boosting events since 2002.";
  const parts = headline.split(/boosting/i);

  return (
    <div data-testid="about-page">
      <Seo title="About" path="/about" description={settings.about_body} />
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">About</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mt-4">
            {parts.length === 2 ? (
              <>
                {parts[0]}
                <span className="bg-ot-peach border-2 border-ot-fg px-3">boosting</span>
                {parts[1]}
              </>
            ) : headline}
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-ot-fg/80">{settings.about_body}</p>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">Our values</p>
        <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter mt-3 leading-none">
          Three words.<br />Lots of follow-through.
        </h2>

        <div className="mt-14 space-y-4 md:space-y-6">
          {(settings.values || []).map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="nb-card p-8 md:p-12 grid md:grid-cols-12 gap-6 md:gap-10 items-start"
              data-testid={`about-value-${i}`}
            >
              <div className="md:col-span-4">
                <span className="font-display text-4xl md:text-5xl tracking-tighter uppercase leading-none block">{v.title}</span>
              </div>
              <p className="md:col-span-8 text-lg md:text-xl">{v.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y-2 border-ot-fg bg-ot-indigo text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-peach">Disciplines</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none mt-3">
            Four teams. One room.
          </h2>
          <div className="grid md:grid-cols-4 gap-4 md:gap-6 mt-12">
            {TEAM.map((t) => (
              <div
                key={t.name}
                className={`${t.color} border-2 border-white text-ot-fg p-6 aspect-square flex flex-col justify-between`}
                data-testid={`team-card-${t.name.toLowerCase()}`}
              >
                <span className="font-display text-2xl uppercase">{t.name}</span>
                <span className="text-sm">{t.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <p className="text-center text-xs uppercase tracking-widest font-bold text-ot-indigo mb-6">Trusted by</p>
        <ClientMarquee />
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="bg-ot-peach border-2 border-ot-fg p-10 md:p-20 shadow-[12px_12px_0px_#111111]">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
            Got an event in the calendar?
          </h2>
          <p className="mt-4 text-lg max-w-xl">We'll review your brief and come back within 24 hours with a first read on approach, timing and budget.</p>
          <Link to="/contact" className="nb-btn mt-8" data-testid="about-cta-contact">
            Start the conversation <ArrowUpRight weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
